import type { ElementType } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";

interface ArticlePreviewCardProps {
  title: string;
  excerpt: string;
  category: string;
  image: string;
  imageAlt: string;
  href?: string;
  meta?: string;
  featured?: boolean;
  ctaLabel?: string;
}

const ArticlePreviewCard = ({
  title,
  excerpt,
  category,
  image,
  imageAlt,
  href,
  meta,
  featured = false,
  ctaLabel = "Read Article",
}: ArticlePreviewCardProps) => {
  const Wrapper = (href ? Link : "article") as ElementType;
  const wrapperProps = href ? { to: href } : {};

  return (
    <Wrapper
      {...(wrapperProps as object)}
      className={cn(
        "group block overflow-hidden border border-border bg-card transition-all duration-500",
        "hover:border-accent/35 hover:shadow-[0_18px_50px_-24px_rgba(0,0,0,0.22)]",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/70 focus-visible:ring-offset-2 focus-visible:ring-offset-background",
        featured && "md:grid md:grid-cols-[1.08fr_0.92fr] md:items-stretch"
      )}
    >
      <div className={cn("overflow-hidden", featured ? "md:min-h-full" : "aspect-[16/10]")}>
        <img
          src={image}
          alt={imageAlt}
          className={cn(
            "h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]",
            featured ? "aspect-[16/11] md:aspect-auto md:min-h-full" : ""
          )}
          loading="lazy"
        />
      </div>

      <div
        className={cn(
          "flex h-full flex-col p-6 md:p-8",
          featured ? "justify-center md:p-10 lg:p-12" : ""
        )}
      >
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-4">
          <span className="font-body text-[10px] uppercase tracking-[0.26em] text-accent">
            {category}
          </span>
          {meta && (
            <span className="font-body text-xs uppercase tracking-[0.16em] text-muted-foreground">
              {meta}
            </span>
          )}
        </div>

        <h3
          className={cn(
            "font-headline text-2xl text-foreground leading-tight mb-3",
            featured && "md:text-3xl lg:text-4xl"
          )}
        >
          {title}
        </h3>

        <p
          className={cn(
            "text-muted-foreground leading-[1.75] flex-1",
            featured ? "text-base md:text-lg" : "text-base"
          )}
        >
          {excerpt}
        </p>

        <div className="pt-6">
          <span className="inline-flex items-center gap-2 font-body text-sm uppercase tracking-[0.14em] text-accent transition-colors group-hover:text-accent/80">
            {ctaLabel}
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
          </span>
        </div>
      </div>
    </Wrapper>
  );
};

export default ArticlePreviewCard;
